
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RevenueScenario } from '../business-opportunity/data';
import { defaultResellerPlan } from '@/hooks/license/resellerPlans/defaultPlans';
import { Check } from 'lucide-react';

interface RevenueScenarioProps {
  scenarios: RevenueScenario[];
}

const ResellerPricing: React.FC<RevenueScenarioProps> = ({ scenarios }) => {
  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
      <h2 className="text-2xl font-bold text-primary-blue mb-6">
        Reseller Pro Plan Features
      </h2>
      
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-3">Plan Details</h3>
          <div className="space-y-2">
            <p className="flex justify-between">
              <span>Annual Plan Price:</span>
              <span className="flex flex-col items-end">
                <span className="text-[#ea384c] line-through font-semibold">${defaultResellerPlan.annualPrice}</span>
                <span className="font-bold text-2xl">$799</span>
              </span>
            </p>
            <p className="flex justify-between">
              <span>Setup Fee:</span>
              <span className="font-semibold">${defaultResellerPlan.setupFee}</span>
            </p>
            <p className="flex justify-between">
              <span>Max Instances:</span>
              <span className="font-semibold">1 (included in this plan)</span>
            </p>
            <p className="flex justify-between">
              <span>Max Accounts per Instance:</span>
              <span className="font-semibold">{defaultResellerPlan.maxAdmins}</span>
            </p>
            <p className="flex justify-between">
              <span>Max Managers:</span>
              <span className="font-semibold">{defaultResellerPlan.maxManagers}</span>
            </p>
            <p className="flex justify-between">
              <span>Max Users per Instance:</span>
              <span className="font-semibold">2,000,000</span>
            </p>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-3">Included Features</h3>
          <ul className="space-y-2">
            {defaultResellerPlan.features.map((feature, index) => (
              feature.included && (
                <li key={index} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span className={feature.highlight ? "font-medium" : ""}>
                    {feature.name}
                  </span>
                </li>
              )
            ))}
          </ul>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-primary-blue mb-6">
        Revenue Potential
      </h2>
      
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-center">Scenario</TableHead>
              <TableHead className="text-center">Signups</TableHead>
              <TableHead className="text-center">Monthly</TableHead>
              <TableHead className="text-center">Your Share</TableHead>
              <TableHead className="text-center">Annual Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scenarios.map(scenario => (
              <TableRow key={scenario.scenario}>
                <TableCell className="text-center font-medium">{scenario.scenario}</TableCell>
                <TableCell className="text-center">{scenario.signups}</TableCell>
                <TableCell className="text-center">{scenario.price}</TableCell>
                <TableCell className="text-center font-bold text-green-600">{scenario.resellerShare}</TableCell>
                <TableCell className="text-center font-bold text-green-600">{scenario.annualShare}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <p className="font-semibold text-primary-blue">Annual Plan: $999</p>
        <p className="font-semibold text-primary-blue">Setup Fee: $500</p>
        <p className="text-sm text-gray-600 mt-2">* Revenue scenarios based on industry standards; actual results may vary based on your execution and sales performance.</p>
      </div>
    </div>
  );
};

export default ResellerPricing;
